"use client";

import { CohortOfferCard } from "@/components/paywall/CohortOfferCard";
import { MembershipOfferCard } from "@/components/paywall/MembershipOfferCard";
import { ThreePlanMembershipCard } from "@/components/paywall/ThreePlanMembershipCard";
import type { CheckoutStatus } from "@/hooks/useRevenueCatCheckoutAttempt";
import { getGuidedCohortLanguageFromCode } from "@/lib/courses/rolling-cohorts";
import {
  PAYWALL_CONTROL_PLAN_ORDER,
  PAYWALL_GUIDED_ANCHOR_VARIANTS,
  type PaywallGuidedAnchorVariant,
} from "@/lib/experiments/paywallGuidedAnchor";
import type { OfferMode } from "@/lib/paywall/user-intent";
import type { PackageType } from "@/lib/revenuecat/config";
import type { PackageDisplayPricing } from "@/lib/revenuecat/package-pricing";

export function PaywallExperimentPricingCards({
  analyticsSource,
  annualSavingsPercent,
  checkoutLocked,
  checkoutStatus,
  cohortEmail,
  cohortFirstName,
  cohortLanguageCode,
  offerMode,
  onBillingChange,
  onCohortAuthRequired,
  onPurchase,
  pricingByPackage,
  selectedBilling,
  selectedPackage,
  variant,
}: {
  analyticsSource: string;
  annualSavingsPercent: number | null;
  checkoutLocked: boolean;
  checkoutStatus: CheckoutStatus;
  cohortEmail: string;
  cohortFirstName?: string | null;
  cohortLanguageCode?: string | null;
  offerMode: OfferMode;
  onBillingChange: (packageType: PackageType) => void;
  onCohortAuthRequired?: () => void;
  onPurchase: (packageType: PackageType) => void;
  pricingByPackage: Record<PackageType, PackageDisplayPricing>;
  selectedBilling: PackageType;
  selectedPackage: PackageType | null;
  variant: PaywallGuidedAnchorVariant;
}) {
  const isPackageLoading = (packageType: PackageType) =>
    selectedPackage === packageType &&
    (checkoutStatus === "preparing" || checkoutStatus === "awaiting_checkout");

  if (variant === PAYWALL_GUIDED_ANCHOR_VARIANTS.control) {
    return (
      <div
        className="mx-auto grid max-w-6xl grid-cols-1 items-stretch gap-5 md:grid-cols-3"
        data-paywall-offers-variant={variant}
      >
        {PAYWALL_CONTROL_PLAN_ORDER.map((packageType) => (
          <ThreePlanMembershipCard
            annualSavingsPercent={annualSavingsPercent}
            disabled={checkoutLocked}
            isLoading={isPackageLoading(packageType)}
            key={packageType}
            onPurchase={onPurchase}
            packageType={packageType}
            pricing={pricingByPackage[packageType]}
            showFreeTrial={
              offerMode === "trial" &&
              packageType !== "weekly" &&
              pricingByPackage[packageType].showFreeTrial
            }
          />
        ))}
      </div>
    );
  }

  const selectedPricing = pricingByPackage[selectedBilling];

  return (
    <div
      className="mx-auto grid max-w-6xl grid-cols-1 items-stretch gap-5 md:grid-cols-2"
      data-paywall-offers-variant={variant}
    >
      <MembershipOfferCard
        annualSavingsPercent={annualSavingsPercent}
        disabled={checkoutLocked}
        isLoading={isPackageLoading(selectedBilling)}
        onBillingChange={onBillingChange}
        onPurchase={onPurchase}
        packageType={selectedBilling}
        pricing={selectedPricing}
        showFreeTrial={
          offerMode === "trial" && selectedBilling !== "weekly" && selectedPricing.showFreeTrial
        }
      />
      <CohortOfferCard
        defaultLanguage={getGuidedCohortLanguageFromCode(cohortLanguageCode)}
        email={cohortEmail}
        firstName={cohortFirstName}
        onAuthRequired={onCohortAuthRequired}
        source={analyticsSource}
      />
    </div>
  );
}
